import type { FieldPath } from 'react-hook-form';
import { type z } from 'zod';
import { string as __eliadmin_zod_0 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import type { ContactMessageReq } from '@eliadmin/core/src/api/forms/forms-api';
import { contactSchema } from '@eliadmin/core/src/components/contact/contact-form-resolver';
import { CONTACT_COPY } from '@eliadmin/core/src/components/dlz/contact/contact-copy';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

/** Figma counter „0 / 1000 znakov“ under the message. */
export const MESSAGE_MAX_LENGTH = 1000;

/**
 * The core contact rules (e-mail, GDPR, attachments, captcha) with the fields of the design: one
 * „Meno a priezvisko“ field instead of name + surname (both still required – split on submit), the
 * optional phone and order number the API accepts, and a required message of at most 1000 characters.
 */
export const contactFormSchema = (VM: FormValidationMessages) =>
  contactSchema(VM)
    .omit({ name: true, surname: true, text: true })
    .extend({
      fullName: __eliadmin_zod_0()
        .trim()
        .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE())
        .refine(value => !value || /\s/.test(value), CONTACT_COPY.fullNameIncomplete),
      phone: __eliadmin_zod_0().trim(),
      order_number: __eliadmin_zod_0().trim(),
      text: __eliadmin_zod_0()
        .trim()
        .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE())
        .max(MESSAGE_MAX_LENGTH, VM.MAX_LENGTH_VALIDATION_MESSAGE(MESSAGE_MAX_LENGTH)),
    });

export type ContactFormInput = z.input<ReturnType<typeof contactFormSchema>>;
export type ContactFormValues = z.output<ReturnType<typeof contactFormSchema>>;

export const CONTACT_DEFAULT_VALUES: ContactFormInput = {
  fullName: '',
  email: '',
  phone: '',
  order_number: '',
  text: '',
  gdpr: false,
  attachments: [],
  'g-recaptcha-response': '',
};

/** Fields of a 422 response → the field that shows the message. */
export const SERVER_FIELD_MAP: Record<string, FieldPath<ContactFormInput>> = {
  name: 'fullName',
  surname: 'fullName',
  email: 'email',
  phone: 'phone',
  order_number: 'order_number',
  text: 'text',
  gdpr: 'gdpr',
  'g-recaptcha-response': 'g-recaptcha-response',
};

/** „Ján Novák“ → name „Ján“, surname „Novák“ (everything after the first word). */
function splitFullName(fullName: string) {
  const [name = '', ...surname] = fullName.split(/\s+/);
  return { name, surname: surname.join(' ') };
}

/** Body of `sendContactMessage` – the core request; empty optional fields are left out. */
export function toContactMessage(
  values: ContactFormValues,
  attachments: { id: number }[],
  captcha: string | undefined,
): ContactMessageReq['data'] {
  return {
    ...splitFullName(values.fullName),
    email: values.email,
    phone: values.phone || undefined,
    order_number: values.order_number || undefined,
    text: values.text,
    gdpr: values.gdpr,
    attachments,
    'g-recaptcha-response': captcha,
  };
}
